// ===================================================
// useMemoryResurfacing.js — 기억 되살리기 + 주간 회고 + 읽지 않은 채팅 알림 훅
//
// 역할:
//   - 로그인 직후(세션 데이터 로드 완료 시점) 기억 되살리기 자동 실행
//     · 하루 최대 2회 / 최근 7일 이내 선정된 기록 제외
//     · AI가 과거 기록 한 편을 골라 채팅 메시지로 자동 저장
//   - 매주 일요일, 지난 한 주 기록을 바탕으로 주간 회고 메시지 자동 생성
//   - 채팅 탭 아이콘에 표시할 "읽지 않음" 빨간 점 상태(hasUnreadChat) 관리
//   - 앱 아이콘 배지(숫자) 업데이트
//   - [B] 기억 되살리기 푸시 예약 (내일 14시)
//   - [D-2] 스트릭 위기 알림 예약
// ===================================================

import { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { appendChatMessage, getTodayResurfacingCount, getRecentResurfacingSessionIds, saveResurfacingLog, getLastAIMessageTime } from '../storage';
import { chatWithMemory, generateMemoryResurfacingIntro, generateDreamFirstStepCheckIn } from '../api/chatApi';
import { generateWeeklyReview } from '../api/sessionApi';
import { setBadgeCount, clearBadge } from '../native/badge';
import { scheduleMemoryResurfacingPush, scheduleStreakDangerPush, scheduleNewChatMessagePush } from '../native/notifications';
import { selectSessionForResurfacing } from '../utils/memoryResurfacing';
import { buildAllSessionContexts } from '../utils/contextMatcher';
import { calculateStreakAtRisk } from '../utils/streakCalculator';
import { appRead, appWrite } from '../utils/appStorage';
import { getToday } from '../utils/dateUtils';
import { triggerSuccessHaptic } from '../native/haptics';

// sessions 객체에서 최근 N일 이내 세션을 평탄화해서 반환
// sessions 형태: { '2026-03-15': [session1, session2], '2026-03-16': [...], ... }
function getSessionsLastNDays(sessions, n) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() - n);

  const result = [];
  Object.entries(sessions).forEach(([date, dayArr]) => {
    const d = new Date(date + 'T00:00:00');
    if (d >= cutoff && d <= today) {
      dayArr.forEach(s => result.push({ ...s, date }));
    }
  });
  return result;
}

export function useMemoryResurfacing({ user, sessions, sessionsLoaded, showToast }) {
  // 나와의 대화 버튼에 빨간 점을 표시할지 여부
  const [hasUnreadChat, setHasUnreadChat] = useState(false);
  // 로그인당 1회만 기억 되살리기를 실행하도록 관리하는 ref
  const initiatedRef = useRef(null);

  // 가장 최근 AI 메시지 시각과 마지막 채팅 열람 시각을 비교해
  // hasUnreadChat 상태와 앱 아이콘 배지를 업데이트하는 내부 함수
  const checkUnreadChat = async (userId) => {
    const lastMsgTime = await getLastAIMessageTime(userId);
    if (!lastMsgTime) {
      clearBadge();
      return;
    }
    const lastOpenedAt = appRead('lastChatOpenedAt');
    const isUnread = !lastOpenedAt || new Date(lastMsgTime) > new Date(lastOpenedAt);
    setHasUnreadChat(isUnread);
    if (isUnread) setBadgeCount(1);
    else clearBadge();
  };

  // 로그아웃 시 기억 되살리기 실행 여부 초기화
  useEffect(() => {
    if (!user) initiatedRef.current = null;
  }, [user]);

  // 로그인 직후, 세션 데이터가 로드된 시점에 기억 되살리기 + 주간 회고를 실행
  // - 로그인당 1회만 실행 (initiatedRef로 관리)
  // - 기억 되살리기: 하루 최대 2회 제한
  // - 주간 회고: 일요일에만, 같은 주 1회만 실행 (localStorage 키로 중복 방지)
  useEffect(() => {
    if (!user || !sessionsLoaded) return;
    if (initiatedRef.current === user.id) return;
    initiatedRef.current = user.id;

    const userId = user.id;
    (async () => {
      // ── 알림 답장 처리 ───────────────────────────────────────
      // AppDelegate.swift가 UserDefaults에 저장해 둔 pending reply를 확인합니다.
      // 있으면: 유저 메시지로 저장 → AI 응답 생성 → 빨간 점 표시 → 이번 세션 종료
      // 없으면: 아래 기억 되살리기 / 주간 회고 로직으로 진행
      if (Capacitor.isNativePlatform()) {
        try {
          const { value: pendingReply } = await Preferences.get({
            key: 'omsal_pending_notification_reply',
          });
          if (pendingReply) {
            // 중복 처리 방지를 위해 즉시 삭제
            await Preferences.remove({ key: 'omsal_pending_notification_reply' });

            // 유저가 알림에서 입력한 텍스트를 채팅 메시지로 저장
            const userMsg = { role: 'user', text: pendingReply };
            await appendChatMessage(userMsg);

            // 기존 기록을 컨텍스트로 활용해 AI 답변 생성
            const sessionContexts = buildAllSessionContexts(sessions);
            const reply = await chatWithMemory(pendingReply, sessionContexts, [userMsg]);
            if (reply) {
              await appendChatMessage({ role: 'assistant', text: reply });
              setHasUnreadChat(true);
              setBadgeCount(1);
              triggerSuccessHaptic();
              if (showToast) showToast('답장에 AI가 응답했어요');
            }
            return; // 이번 세션은 알림 답장 처리만 하고 종료
          }
        } catch {
          // 알림 답장 처리 실패 시 조용히 무시하고 아래 로직 진행
        }
      }

      try {
        // ── 주간 회고 (일요일 자동 트리거) ──────────────────────
        const today = new Date();
        if (today.getDay() === 0) { // 0 = 일요일
          const todayStr = getToday();
          const weeklyKey = `omsal_weekly_review_${todayStr}`;
          const alreadyDone = appRead(weeklyKey);

          if (!alreadyDone) {
            const weekSessions = getSessionsLastNDays(sessions, 7);
            if (weekSessions.length >= 1) {
              const reviewText = await generateWeeklyReview(weekSessions);
              await appendChatMessage({
                role: 'assistant',
                text: reviewText,
              });
              appWrite(weeklyKey, 'true');
              setHasUnreadChat(true);
              setBadgeCount(1);
              triggerSuccessHaptic();
              if (showToast) showToast('📝 이번 주 이야기 정리가 도착했어');
              scheduleNewChatMessagePush(); // [I] 5분 안에 채팅 안 열면 알림
            }
          }
        }

        // ── 기억 되살리기 ──────────────────────────────────────
        // 1 & 2를 병렬로 조회 (독립적인 DB 쿼리이므로 동시에 실행)
        const [todayCount, recentIds] = await Promise.all([
          getTodayResurfacingCount(userId),
          getRecentResurfacingSessionIds(userId, 7),
        ]);

        // 오늘 이미 2회 이상 실행됐으면 건너뜀
        if (todayCount >= 2) {
          await checkUnreadChat(userId);
          return;
        }

        // 3. 기억 되살리기 대상 세션 선정
        const targetSession = selectSessionForResurfacing(sessions, recentIds);
        if (!targetSession) {
          await checkUnreadChat(userId);
          return;
        }

        // 4. AI 첫 메시지 생성 (GPT 호출)
        const introText = await generateMemoryResurfacingIntro(targetSession);

        // 5. 채팅 메시지로 저장 (기억 되살리기 플래그 포함)
        await appendChatMessage({
          role: 'assistant',
          text: introText,
          memorySessionId: targetSession.id,
          isMemoryResurfacing: true,
        });

        // 6. 기억 되살리기 로그 저장 (재선정 방지용)
        await saveResurfacingLog(userId, targetSession.id, targetSession.date);

        // 7. 빨간 점 표시 + 앱 아이콘 배지 1로 설정 + 햅틱 + 토스트 알림
        setHasUnreadChat(true);
        setBadgeCount(1);
        triggerSuccessHaptic();
        if (showToast) showToast('💬 새로운 이야기가 도착했어');
        scheduleNewChatMessagePush(); // [I] 5분 안에 채팅 안 열면 알림

        // 8. [B] 내일 14시에 기억 되살리기 푸시 예약
        //    → 앱을 내일 열면 App.js에서 취소하므로, 안 열었을 때만 도착
        const targetDate = new Date(targetSession.date + 'T00:00:00');
        const todayDate = new Date();
        todayDate.setHours(0, 0, 0, 0);
        const daysAgo = Math.round((todayDate - targetDate) / (1000 * 60 * 60 * 24));
        scheduleMemoryResurfacingPush(daysAgo);
      } catch {
        // 오류 발생 시 조용히 무시 (사용자 경험에 영향 없도록)
      }

      // ── 꿈과 목표 첫 행동 점검 ─────────────────────────────
      // dream 모드로 저장한 세션 중 7일 이상 지났고, firstStep이 있고,
      // 아직 점검 메시지를 보내지 않은 세션에 대해 1회 자동 점검
      try {
        const dreamCheckKey = 'omsal_dream_checkin_done';
        const checkedIds = JSON.parse(appRead(dreamCheckKey) || '[]');

        const allSessions = Object.entries(sessions).flatMap(([date, arr]) =>
          arr.map(s => ({ ...s, date }))
        );
        const todayDate = new Date();
        todayDate.setHours(0, 0, 0, 0);

        const dreamCandidate = allSessions.find(s => {
          if (s.mode !== 'dream') return false;
          const vm = s.visionMeta || s.vision_meta;
          if (!vm?.firstStep) return false;
          if (checkedIds.includes(s.id)) return false;
          const sessionDate = new Date(s.date + 'T00:00:00');
          const daysAgo = Math.round((todayDate - sessionDate) / (1000 * 60 * 60 * 24));
          return daysAgo >= 7;
        });

        if (dreamCandidate) {
          const vm = dreamCandidate.visionMeta || dreamCandidate.vision_meta;
          const sessionForApi = {
            ...dreamCandidate,
            visionMeta: vm,
          };
          const checkInText = await generateDreamFirstStepCheckIn(sessionForApi);

          await appendChatMessage({
            role: 'assistant',
            text: checkInText,
            memorySessionId: dreamCandidate.id,
            isMemoryResurfacing: true,
          });

          // 점검 완료 기록 (같은 세션에 대해 중복 방지)
          const updatedIds = [...checkedIds, dreamCandidate.id].slice(-20);
          appWrite(dreamCheckKey, JSON.stringify(updatedIds));

          setHasUnreadChat(true);
          setBadgeCount(1);
          triggerSuccessHaptic();
          if (showToast) showToast('🌱 지난주 첫 걸음, 어떻게 됐어?');
          scheduleNewChatMessagePush();
        }
      } catch {
        // dream 첫 행동 점검 실패 시 무시
      }

      // ── [D-2] 스트릭 위기 알림 ─────────────────────────────
      // 오늘 기록이 없고, 어제까지 3일 이상 연속 기록했으면 위기 알림 예약
      try {
        const streakAtRisk = calculateStreakAtRisk(sessions);
        if (streakAtRisk >= 3) {
          const reminderHour = parseInt(appRead('omsal_reminder_hour') || '21', 10);
          scheduleStreakDangerPush(streakAtRisk, reminderHour);
        }
      } catch {
        // 스트릭 위기 알림 실패 시 무시
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, sessionsLoaded]);

  return { hasUnreadChat, setHasUnreadChat };
}
